import React, {memo, useEffect, useRef, useState} from 'react';
import ReactApexChart from 'react-apexcharts';
import post from '../utils/post.js';
import ConfirmPopup from './ConfirmPopup.js';
import FormSelect from './FormSelect.js';

const HOUR_LABELS = Array.from({length: 24}, (_, hour) => `${hour.toString().padStart(2, '0')}:00`).reverse();
const DAY_LABELS = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
const MONTH_OPTIONS = [
	{value: 1, label: 'Leden'},
	{value: 2, label: 'Únor'},
	{value: 3, label: 'Březen'},
	{value: 4, label: 'Duben'},
	{value: 5, label: 'Květen'},
	{value: 6, label: 'Červen'},
	{value: 7, label: 'Červenec'},
	{value: 8, label: 'Srpen'},
	{value: 9, label: 'Září'},
	{value: 10, label: 'Říjen'},
	{value: 11, label: 'Listopad'},
	{value: 12, label: 'Prosinec'},
];

function getCurrentMonthKey() {
	const now = new Date();
	return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function buildMonthLabel(monthKey) {
	const [year, monthValue] = String(monthKey || '').split('-');
	const month = Number(monthValue);
	const monthLabel = MONTH_OPTIONS.find((item) => item.value === month)?.label || '';

	return year && monthLabel ? `${year} ${monthLabel}` : '';
}

function normalizeMonthOptions(monthOptions) {
	if (!Array.isArray(monthOptions) || monthOptions.length === 0) {
		const currentMonthKey = getCurrentMonthKey();

		return [{key: currentMonthKey, label: buildMonthLabel(currentMonthKey)}];
	}

	return monthOptions.map((option) => ({
		key: option.key,
		label: buildMonthLabel(option.key),
	}));
}

function formatNumber(value) {
	return Math.round(Number(value) || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

function getVisitLabel(count) {
	const absCount = Math.abs(Number(count) || 0);
	const mod100 = absCount % 100;
	const mod10 = absCount % 10;

	if (mod100 >= 11 && mod100 <= 14) {
		return 'návštěv';
	}

	if (mod10 === 1) {
		return 'návštěva';
	}

	if (mod10 >= 2 && mod10 <= 4) {
		return 'návštěvy';
	}

	return 'návštěv';
}

function createEmptySeries() {
	return HOUR_LABELS.map((hourLabel) => ({
		name: hourLabel,
		data: DAY_LABELS.map((dayLabel) => ({x: dayLabel, y: 0, absolute: 0})),
	}));
}

function mapSeriesForDisplay(series, absoluteMax) {
	return (series || []).map((seriesItem) => ({
		name: seriesItem.name,
		data: (seriesItem.data || []).map((point) => {
			const absolute = Number(point.y) || 0;

			return {
				x: point.x,
				y: absolute,
				absolute: absolute,
			};
		}),
	}));
}

function getPeakCellInfo(series) {
	let peakPoint = null;

	(series || []).forEach((seriesItem) => {
		(seriesItem.data || []).forEach((point) => {
			const absolute = Number(point.y) || 0;

			if (!peakPoint || absolute > peakPoint.absolute) {
				peakPoint = {
					absolute,
					dayLabel: point.x || '',
					hourLabel: seriesItem.name || '',
				};
			}
		});
	});

	return peakPoint;
}

function buildChartOptions(maxValue) {
	const upperBound = Math.max(1, Number(maxValue) || 0);
	const firstBand = Math.max(1, Math.ceil(upperBound * 0.25));
	const secondBand = Math.max(firstBand + 1, Math.ceil(upperBound * 0.5));
	const thirdBand = Math.max(secondBand + 1, Math.ceil(upperBound * 0.75));
	const ranges = [{from: 0, to: 0, color: '#777777', foreColor: '#dddddd', name: 'Bez návštěv'}];

	if (upperBound >= 1) {
		ranges.push({from: 1, to: Math.min(firstBand, upperBound), color: '#87b28f', foreColor: '#ffffff', name: 'Nízké'});
	}

	if (upperBound > firstBand) {
		ranges.push({from: firstBand + 1, to: Math.min(secondBand, upperBound), color: '#d8c483', foreColor: '#43381c', name: 'Střední'});
	}

	if (upperBound > secondBand) {
		ranges.push({from: secondBand + 1, to: Math.min(thirdBand, upperBound), color: '#d89d87', foreColor: '#ffffff', name: 'Vyšší'});
	}

	if (upperBound > thirdBand) {
		ranges.push({from: thirdBand + 1, to: upperBound, color: '#c77d88', foreColor: '#ffffff', name: 'Špička'});
	}

	return {
		chart: {
			type: 'heatmap',
			toolbar: {show: false},
			animations: {enabled: true},
			background: 'transparent',
			offsetY: -10,
			parentHeightOffset: 0,
		},
		legend: {show: false},
		plotOptions: {
			heatmap: {
				radius: 5,
				enableShades: false,
				colorScale: {
					ranges: ranges,
				},
			},
		},
		dataLabels: {
			enabled: true,
			textAnchor: 'middle',
			offsetX: 0,
			offsetY: 0,
			style: {
				fontSize: '1.4rem',
				fontWeight: 600,
			},
			formatter: function (value, opts) {
				if (value <= 0) {
					return '';
				}

				const point = opts.w.config.series?.[opts.seriesIndex]?.data?.[opts.dataPointIndex];
				return formatNumber(point?.absolute || value);
			},
		},
		xaxis: {
			position: 'top',
			labels: {
				rotate: 0,
				offsetY: 4,
				style: {
					colors: '#d4d4d4',
					fontSize: '1.4rem',
				},
			},
			crosshairs: {
				show: false,
			},
			axisBorder: {show: false},
			axisTicks: {show: false},
		},
		yaxis: {
			labels: {
				offsetX: -6,
				offsetY: 0,
				style: {
					colors: '#f0f0f0',
					fontSize: '1.4rem',
				},
			},
		},
		grid: {
			borderColor: '#3E3E3E',
			xaxis: {
				lines: {show: false},
			},
			yaxis: {
				lines: {show: false},
			},
			padding: {left: 8, right: 4, top: 0, bottom: 0},
		},
		stroke: {
			width: 1,
			colors: ['#3E3E3E'],
		},
		tooltip: {
			theme: 'dark',
			x: {
				show: false,
			},
			marker: {
				show: false,
			},
			y: {
				title: {
					formatter: function () {
						return '';
					},
				},
			},
			custom: function ({seriesIndex, dataPointIndex, w}) {
				const point = w.config.series?.[seriesIndex]?.data?.[dataPointIndex];
				const hourLabel = w.config.series?.[seriesIndex]?.name || '';
				const visits = Math.round(point?.absolute || 0);

				return `<div class="home-occupancy-popup__tooltip"><div><b>${point?.x || ''}, ${hourLabel}</b></div><div>${formatNumber(visits)} ${getVisitLabel(visits)}</div></div>`;
			},
		},
	};
}

function HomeOccupancyHeatmap({show = false, close = () => {}}) {
	const currentMonthKey = getCurrentMonthKey();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [absoluteSeries, setAbsoluteSeries] = useState(createEmptySeries());
	const [absoluteMaxValue, setAbsoluteMaxValue] = useState(0);
	const [hasLoadedData, setHasLoadedData] = useState(false);
	const [filterType, setFilterType] = useState('all');
	const [monthKey, setMonthKey] = useState(currentMonthKey);
	const [monthOptions, setMonthOptions] = useState(normalizeMonthOptions());
	const hasLoadedDataRef = useRef(false);

	useEffect(() => {
		if (!show) {
			setLoading(false);
			setError('');
			hasLoadedDataRef.current = false;
			setHasLoadedData(false);
			return;
		}

		setLoading(true);
		setError('');

		post('getReservationOccupancyHeatmap', {FILTERTYPE: filterType, MONTHKEY: monthKey})
			.then((res) => {
				const responseData = res.data || {};
				setAbsoluteSeries(responseData.series?.length > 0 ? responseData.series : createEmptySeries());
				setAbsoluteMaxValue(responseData.maxValue || 0);
				setMonthOptions(normalizeMonthOptions(responseData.monthOptions));
				if (responseData.monthKey) {
					setMonthKey(responseData.monthKey);
				}
				hasLoadedDataRef.current = true;
				setHasLoadedData(true);
				setLoading(false);
			})
			.catch((err) => {
				console.error(err);
				setError('Nepodařilo se načíst data vytíženosti.');
				if (!hasLoadedDataRef.current) {
					setAbsoluteSeries(createEmptySeries());
					setAbsoluteMaxValue(0);
				}
				setLoading(false);
			});
	}, [filterType, monthKey, show]);

	if (!show) {
		return null;
	}

	const series = mapSeriesForDisplay(absoluteSeries, absoluteMaxValue);
	const totalVisits = absoluteSeries.reduce((dayTotal, day) => {
		return dayTotal + day.data.reduce((hourTotal, hour) => hourTotal + (Number(hour.y) || 0), 0);
	}, 0);
	const peakCellInfo = getPeakCellInfo(absoluteSeries);
	const activeMonthLabel = monthOptions.find((item) => item.key === monthKey)?.label || buildMonthLabel(monthKey);
	const monthSelectOptions = monthOptions.map((item) => ({ID: item.key, NAME: item.label}));

	const handleMonthFilterClick = () => {
		setMonthKey(currentMonthKey);
		setFilterType('month');
	};

	const handleMonthSelect = (nextMonth) => {
		setMonthKey(nextMonth.ID);
	};

	return (
		<ConfirmPopup
			showConfirmPopup={show}
			closeConfirmPopup={close}
			infoHeader={
				<>
					<div className="home-occupancy-popup__title">Vytíženost podle dne a hodiny</div>
				</>
			}
			infoMain=""
			popupContentClassName="home-occupancy-popup__content"
			bodyClassName="home-occupancy-popup__body"
			bodyInnerClassName="home-occupancy-popup__body-in"
		>
			<div className="home-occupancy-popup__controls">
				<div className="home-occupancy-popup__group">
					<button className={`button home-occupancy-popup__filter-button ${filterType === 'all' ? 'green' : ''}`} onClick={() => setFilterType('all')}>
						Historie
					</button>
					<button className={`button home-occupancy-popup__filter-button ${filterType === 'month' ? 'green' : ''}`} onClick={handleMonthFilterClick}>
						Vyber měsíc
					</button>
					<button className={`button home-occupancy-popup__filter-button ${filterType === 'last_week' ? 'green' : ''}`} onClick={() => setFilterType('last_week')}>
						Minulý týden
					</button>
				</div>

				{filterType === 'month' ? (
					<div className="home-occupancy-popup__select-wrap">
						<FormSelect label="" value={monthKey} valueLabel={activeMonthLabel} setValue={handleMonthSelect} options={monthSelectOptions} showNone={false} error="" noBottomPadding />
					</div>
				) : (
					''
				)}
			</div>

			<div className="home-occupancy-popup__summary">
				<span>Celkem návštěv: <b>{formatNumber(totalVisits)}</b></span>
				<span>
					Nejvyšší buňka:{' '}
					<b>
						{formatNumber(absoluteMaxValue)}
						{peakCellInfo && peakCellInfo.absolute > 0 ? ` (${peakCellInfo.dayLabel} ${peakCellInfo.hourLabel})` : ''}
					</b>
				</span>
			</div>

			{loading && !hasLoadedData ? <div className="home-occupancy-popup__state">Načítám heatmapu...</div> : ''}
			{!loading && error !== '' && !hasLoadedData ? <div className="home-occupancy-popup__state error">{error}</div> : ''}
			{error === '' || hasLoadedData ? (
				<div className="home-occupancy-popup__chart-wrap">
					<ReactApexChart options={buildChartOptions(absoluteMaxValue)} series={series} type="heatmap" height="560" />
				</div>
			) : (
				''
			)}
		</ConfirmPopup>
	);
}

export default memo(HomeOccupancyHeatmap);